import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "@/lib/googleMapsLoader";
import { AlertTriangle } from "lucide-react";

// Declaração global do Google Maps
declare global {
  interface Window {
    google: any;
  }
}

interface GoogleMapViewProps {
  center: [number, number];
  zoom?: number;
  height?: string;
  markerTitle?: string;
  markerAddress?: string;
  originalCoordinates?: [number, number] | null;
  originalAddress?: string;
}

export const GoogleMapView = ({
  center,
  zoom = 16,
  height = "300px",
  markerTitle = "Localização",
  markerAddress,
  originalCoordinates = null,
  originalAddress,
}: GoogleMapViewProps) => {
  const mapRef = useRef<any>(null);
  const currentMarkerRef = useRef<any>(null);
  const originalMarkerRef = useRef<any>(null);
  const infoWindowRef = useRef<any>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const initMap = () => {
      try {
        if (!center) return;

        const mapElement = document.getElementById("google-map-view");
        if (!mapElement || mapRef.current) return;

        // Validar coordenadas
        if (
          !center ||
          center.length !== 2 ||
          typeof center[0] !== "number" ||
          typeof center[1] !== "number" ||
          isNaN(center[0]) ||
          isNaN(center[1])
        ) {
          console.error("Coordenadas inválidas:", center);
          setHasError(true);
          return;
        }

        // Verificar se o Google Maps está disponível
        if (!window.google || !window.google.maps) {
          console.error("Google Maps não está disponível");
          setHasError(true);
          return;
        }

        // Criar mapa
        const map = new window.google.maps.Map(mapElement, {
          center: { lat: center[0], lng: center[1] },
          zoom: zoom,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          mapId: "GOOGLE_MAP_VIEW",
        });

        // Se houver coordenadas originais, criar marcador vermelho
        if (
          originalCoordinates &&
          originalCoordinates.length === 2 &&
          typeof originalCoordinates[0] === "number" &&
          typeof originalCoordinates[1] === "number" &&
          !isNaN(originalCoordinates[0]) &&
          !isNaN(originalCoordinates[1])
        ) {
          const pinBackground = new window.google.maps.marker.PinElement({
            background: "#DC2626",
            borderColor: "#991B1B",
            glyphColor: "#FFFFFF",
          });

          const originalMarker =
            new window.google.maps.marker.AdvancedMarkerElement({
              position: {
                lat: originalCoordinates[0],
                lng: originalCoordinates[1],
              },
              map: map,
              content: pinBackground.element,
              title: originalAddress || "Localização Original",
            });

          originalMarkerRef.current = originalMarker;

          // Ajustar bounds para mostrar ambos os marcadores
          const bounds = new window.google.maps.LatLngBounds();
          bounds.extend({
            lat: originalCoordinates[0],
            lng: originalCoordinates[1],
          });
          bounds.extend({ lat: center[0], lng: center[1] });
          map.fitBounds(bounds);

          // Adicionar padding
          setTimeout(() => {
            map.panBy(0, -50);
          }, 100);
        }

        // Criar marcador atual (verde se houver original, padrão se não)
        const currentPinBackground = originalCoordinates
          ? new window.google.maps.marker.PinElement({
              background: "#16A34A",
              borderColor: "#15803D",
              glyphColor: "#FFFFFF",
            })
          : undefined;

        const currentMarker =
          new window.google.maps.marker.AdvancedMarkerElement({
            position: { lat: center[0], lng: center[1] },
            map: map,
            content: currentPinBackground?.element,
            title: originalCoordinates ? "Localização Modificada" : markerTitle,
          });

        // Função para criar InfoWindow com endereço
        const createInfoWindow = (address: string, title: string) => {
          return new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 8px; max-width: 280px;">
                <h4 style="font-weight: 600; font-size: 13px; margin-bottom: 4px;">${title}</h4>
                <p style="font-size: 12px; color: #4b5563; word-wrap: break-word;">📍 ${address}</p>
              </div>
            `,
          });
        };

        // Reverse geocode de coordenadas para endereço
        const reverseGeocode = (coords: [number, number], callback: (addr: string) => void) => {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            { location: { lat: coords[0], lng: coords[1] } },
            (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                callback(results[0].formatted_address);
              } else {
                callback(`${coords[0].toFixed(6)}, ${coords[1].toFixed(6)}`);
              }
            },
          );
        };

        // InfoWindow no marcador atual
        currentMarker.addListener("click", () => {
          if (infoWindowRef.current) infoWindowRef.current.close();

          const title = originalCoordinates ? "Localização Modificada" : markerTitle;

          if (markerAddress) {
            const iw = createInfoWindow(markerAddress, title);
            iw.open({ anchor: currentMarker, map });
            infoWindowRef.current = iw;
          } else {
            reverseGeocode(center, (addr) => {
              const iw = createInfoWindow(addr, title);
              iw.open({ anchor: currentMarker, map });
              infoWindowRef.current = iw;
            });
          }
        });

        // InfoWindow no marcador original
        if (originalMarkerRef.current) {
          originalMarkerRef.current.addListener("click", () => {
            if (infoWindowRef.current) infoWindowRef.current.close();

            if (originalAddress) {
              const iw = createInfoWindow(originalAddress, "Localização Original");
              iw.open({ anchor: originalMarkerRef.current, map });
              infoWindowRef.current = iw;
            } else if (originalCoordinates) {
              reverseGeocode(originalCoordinates, (addr) => {
                const iw = createInfoWindow(addr, "Localização Original");
                iw.open({ anchor: originalMarkerRef.current, map });
                infoWindowRef.current = iw;
              });
            }
          });
        }

        mapRef.current = map;
        currentMarkerRef.current = currentMarker;
      } catch (error) {
        console.error("Erro ao inicializar mapa:", error);
        setHasError(true);
      }
    };

    // Carregar Google Maps usando loader global
    loadGoogleMaps(initMap);
  }, [center, zoom, markerTitle, markerAddress, originalCoordinates, originalAddress]);

  useEffect(() => {
    if (
      mapRef.current &&
      currentMarkerRef.current &&
      center &&
      center.length === 2 &&
      typeof center[0] === "number" &&
      typeof center[1] === "number" &&
      !isNaN(center[0]) &&
      !isNaN(center[1])
    ) {
      try {
        const position = { lat: center[0], lng: center[1] };

        // Se não houver marcador original, centralizar no marcador atual
        if (!originalCoordinates) {
          mapRef.current.setCenter(position);
        }

        currentMarkerRef.current.position = position;
      } catch (error) {
        console.error("Erro ao atualizar posição do mapa:", error);
        setHasError(true);
      }
    }
  }, [center, originalCoordinates]);

  // Renderizar mensagem de erro se houver falha
  if (hasError) {
    return (
      <div
        style={{ height, width: "100%" }}
        className="rounded-lg overflow-hidden flex items-center justify-center bg-gray-100 border border-gray-200"
      >
        <div className="text-center p-4">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-700 mb-1">
            Não foi possível carregar o mapa
          </p>
          <p className="text-xs text-gray-500">
            Verifique sua conexão ou tente recarregar a página
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      id="google-map-view"
      style={{ height, width: "100%" }}
      className="rounded-lg overflow-hidden"
    />
  );
};
